// ============================================================================
// CustomListFormRenderer — рендер кастомной формы СПИСКА (макет из layout).
// ============================================================================
// Соглашения (как в 1С):
//   - динамический список (dynamicList) — основная таблица списка;
//     колонки берутся из element.columns (поля = физические колонки);
//   - поля (field) — колонки таблицы, если у динамического списка колонки не заданы;
//   - группы/метки — оформление вокруг таблицы;
//   - кнопки в формах списка пока НЕ поддерживаются — рендерятся отключёнными.
// Никаких фоллбэков: неизвестный элемент → явная ошибка.
// ============================================================================

import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { DynamicListTable } from '@/components/forms/DynamicListTable';

interface CustomListFormRendererProps {
  /** Элементы макета формы списка */
  elements: any[];
  objectType: string;
  objectName: string;
  onRowDoubleClick?: (row: Record<string, unknown>) => void;
  /** Одиночный клик по строке (для форм выбора — выбор значения) */
  onSelect?: (row: Record<string, unknown>) => void;
  onCreate?: () => void;
  /** Реквизиты формы (для dynamic_list нужен source с таблицей/отборами/командами) */
  formAttrs?: Array<{ name: string; isMain?: boolean; source?: { type?: string; table?: string; filters?: Array<{ field: string; condition: string; value?: string }>; commands?: string[] } }>;
  /** Дополнительные отборы списка (например owner_id = текущая запись во вкладке подчинённых) —
      объединяются с отборами формы списка */
  extraFilters?: Array<{ field: string; condition: string; value?: string }>;
}

function columnKey(attribute: string): string {
  // Поле может быть путём «Объект.наименование» — берём последний сегмент (физическую колонку)
  const last = attribute.split('.').pop() || '';
  return last.trim();
}

export function CustomListFormRenderer({
  elements, objectType, objectName, onRowDoubleClick, onSelect, onCreate, formAttrs = [], extraFilters,
}: CustomListFormRendererProps) {
  // Главный реквизит формы — динамический список (как в 1С: форма списка = динамический список).
  // Рендерится ВСЕГДА, даже если в макете нет элемента dynamicList (пустой макет — не пустая форма).
  const mainDlAttr = (formAttrs || []).find((a) => a.isMain && a.source?.type === 'base_table') ||
    (formAttrs || []).find((a) => a.source?.type === 'base_table');

  const dlElement = elements.find((el) => el.type === 'dynamicList');
  const fieldElements = elements.filter((el) => el.type === 'field');

  // Колонки: из динамического списка (element.columns) или из полей макета
  let visibleKeys: string[] | undefined;
  if (dlElement?.columns?.length > 0) {
    visibleKeys = dlElement.columns.map((c: any) => columnKey(c.field)).filter(Boolean);
  } else if (fieldElements.length > 0) {
    visibleKeys = fieldElements.map((el) => columnKey(el.attribute || '')).filter(Boolean);
  }

  // Источник динамического списка (таблица/запрос/отборы/команды):
  // приоритет — реквизит формы (source), затем сам элемент
  const dlAttr = (formAttrs || []).find((a) => a.name === (dlElement?.attribute || ''));
  const source = dlAttr?.source || dlElement?.source || mainDlAttr?.source;
  const sourceTable = source?.table || objectName;
  // Отборы: отборы формы списка + дополнительные (SSoT: владелец вкладки подчинённых)
  const filters = [...((source?.filters as any) || []), ...(extraFilters || [])];
  const commands = source?.commands;
  const isQuerySource = source?.type === 'query' && !!source?.query;

  // Колонки динамического списка (для запросов и явно заданных колонок)
  const dlColumns = (dlElement?.columns || []).map((c: any) => ({
    field: columnKey(c.field),
    synonym: c.synonym || c.field,
    width: c.width,
    visible: c.visible !== false,
  }));

  const renderDynamicList = (key: string) => {
    if (isQuerySource) {
      // Динамический список по запросу hydraql — колонки могут быть произвольными
      return (
        <div key={key} className="flex-1 min-h-0 flex flex-col">
          {dlElement?.label && <Label className="text-xs mb-1">{dlElement.label}</Label>}
          <div className="flex-1 min-h-0 overflow-auto">
            <DynamicListTable
              query={source?.query}
              columns={dlColumns}
              onRowClick={onRowDoubleClick}
            />
          </div>
        </div>
      );
    }
    return (
      <div key={key} className="flex-1 min-h-0 flex flex-col">
        {dlElement?.label && dlElement.showLabel !== false && <Label className="text-xs mb-1">{dlElement.label}</Label>}
        <div className="flex-1 min-h-0 border rounded-md overflow-hidden">
          <AutoListForm
            objectType={objectType}
            objectName={objectName}
            table={sourceTable}
            visibleKeys={visibleKeys}
            filters={filters}
            commands={commands}
            viewMode={dlElement?.viewMode as any}
            groupMode={typeof dlElement?.groupMode === 'string' ? dlElement.groupMode : ''}
            groupBy={Array.isArray(dlElement?.groupBy) ? dlElement.groupBy.join(',') : (typeof dlElement?.groupBy === 'string' ? dlElement.groupBy : '')}
            onRowDoubleClick={onRowDoubleClick}
            onSelect={onSelect}
            onCreate={onCreate}
          />
        </div>
      </div>
    );
  };

  const hasLayoutDl = elements.some((el) => el.type === 'dynamicList');

  // Пустая форма списка (нет элементов макета И нет главного реквизита динамического списка):
  // явный хинт вместо тихого пустого экрана — как в объектной форме «Макет формы пуст».
  if (elements.length === 0 && !mainDlAttr) {
    return (
      <div className="flex flex-col flex-1 min-h-0 gap-3 w-full">
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
          Макет формы списка пуст — добавьте главный реквизит «Динамический список» в дизайнере формы
        </div>
      </div>
    );
  }

  // Защита от рекурсивного рендера макета (повреждённая вложенность групп) —
  // переполнение стека роняло список. MAX_LAYOUT_DEPTH=15; превышение — ЯВНАЯ
  // ошибка.
  const MAX_LAYOUT_DEPTH = 15;
  const renderElement = (el: any, idx: number, depth = 0): React.ReactNode => {
    if (depth > MAX_LAYOUT_DEPTH) {
      console.error('Layout depth exceeded (макет формы списка повреждён?)', el);
      return (
        <div key={`depth-${idx}`} className="text-xs text-destructive border border-destructive/30 rounded p-2 bg-destructive/5">
          Ошибка: слишком глубокая вложенность макета формы (более {MAX_LAYOUT_DEPTH} уровней).
        </div>
      );
    }
    switch (el.type) {
      case 'dynamicList':
        return renderDynamicList(`dl-${idx}`);
      case 'field':
        // Поля макета = колонки таблицы (обработаны через visibleKeys); отдельно не рендерятся
        return null;
      case 'label':
        return <div key={`l-${idx}`} className="py-1"><Label className="text-xs">{el.text || ''}</Label></div>;
      case 'group': {
        const children = el.elements || [];
        return (
          <div key={`g-${idx}`} className="flex flex-col gap-2">
            {el.title && <div className="text-xs font-medium text-muted-foreground">{el.title}</div>}
            <div className={`flex gap-2 ${el.direction === 'horizontal' ? 'flex-row flex-wrap' : 'flex-col'}`}>
              {children.map((child: any, ci: number) => renderElement(child, ci, depth + 1))}
            </div>
          </div>
        );
      }
      case 'button':
        // Команды форм списка не поддерживаются — кнопка явно отключена (без фоллбэков)
        return (
          <div key={`b-${idx}`} className="py-1" title="Кнопки в форме списка не поддерживаются">
            <Button size="sm" variant="outline" disabled>
              {el.label || el.command || el.onClick || 'Кнопка'}
            </Button>
          </div>
        );
      default:
        return <div key={`unknown-${idx}`} className="text-xs text-destructive">Неизвестный элемент формы: {el.type}</div>;
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3 w-full">
      {elements.map((el, idx) => renderElement(el, idx, 0))}
      {/* Главный реквизит (динамический список) рендерится всегда — форма списка не может быть пустой */}
      {!hasLayoutDl && mainDlAttr && renderDynamicList('dl-main')}
    </div>
  );
}
